import React, { useEffect, useRef, useState } from "react";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import Button from "shared/components/buttons/Button";
import { ICONS } from "shared/components/Icon";
import InputText from "shared/components/inputs/InputText";
import getImageUrl from "shared/helpers/getImageUrl";
import axios from "axios";
import { snackbarSuccess } from "shared/redux/actions/Snackbar";
import Icon from "shared/components/Icon";
import uploadImage from "helpers/getPublicImageUrl";
import "./style.scss";
import OkWarning from "shared/components/OkWarning";

const IntegrationForm = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { osbn } = useParams();
    const fileRef = useRef(null);
    const { control, handleSubmit, errors, reset } = useForm();

    const { books } = useSelector(state => state.books);
    const { profile } = useSelector(state => state.user);
    const { current: organisation } = useSelector(state => state.organisation);
    const book = books[osbn];
    const [coverImage, setCoverImage] = useState(book?.cover);

    const [initialState, setInitialState] = useState({
        includePicture: false,
        includeBookDescription: false,
        includeAuthorDescription: false,
        websites: [{ name: "Website 1", value: "" }],
    });

    const [includePicture, setIncludePicture] = useState(false);
    const [includeBookDescription, setIncludeBookDescription] = useState(false);
    const [includeAuthorDescription, setIncludeAuthorDescription] = useState(false);
    const [websites, setWebsites] = useState([{ name: "Website 1", value: "" }]);
    const [isEditing, setIsEditing] = useState(false);
    const [widgetLink, setWidgetLink] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [widgetId, setWidgetId] = useState("");
    const [widgetExists, setWidgetExists] = useState(false);
    const loadImage = async () => {
        if (book?.cover) {
            if (!book.cover.includes("https")) {
                const imageUrl = await getImageUrl(book.cover, organisation.id);
                setCoverImage(imageUrl);
            }
        }
    };

    const isDev = (!process.env.NODE_ENV || process.env.NODE_ENV === "development");
    const API_BASE_URL = isDev
        ? "http://localhost:1337"
        : window.location.href.includes("acceptance")
            ? "https://acceptance.okcomply.com"
            : "https://my.okcomply.com";

    const prefillForm = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/invites/iframe-template/${book.id}`);
            const widget = response.data;

            setInitialState({
                includePicture: widget.hasPicture,
                includeBookDescription: widget.hasDescription,
                includeAuthorDescription: widget.hasAuthor,
                websites: widget.websites.length > 0 ? widget.websites : [{ name: "Website 1", value: "" }],
            });

            setIncludePicture(widget.hasPicture);
            setIncludeBookDescription(widget.hasDescription);
            setIncludeAuthorDescription(widget.hasAuthor);
            setWidgetId(widget.id);
            setWebsites(widget.websites.length > 0 ? widget.websites : [{ name: "Website 1", value: "" }]);
            const widgetLink = `${API_BASE_URL}/invites/iframe-content/${widget.id}`;
            const iframeElement = `<iframe src="${widgetLink}" width="550" height="650"></iframe>`;
            setWidgetLink(iframeElement);
            setWidgetExists(true);
        } catch (error) {
            console.error("Failed to prefill form:", error.response?.data || error.message);
        }
    };

    useEffect(() => {
        loadImage();
        prefillForm();
    }, []);

    const handleAddWebsite = () => {
        const newWebsiteNumber = websites.length + 1;
        setWebsites([
            ...websites,
            { name: `Website ${newWebsiteNumber}`, value: "" },
        ]);
    };



    const handleRemoveWebsite = (index) => {
        setWebsites(websites.filter((_, i) => i !== index));
    };

    const handleChange = (index, newValue) => {
        const updatedWebsites = websites.map((website, i) =>
            i === index ? { ...website, value: newValue } : website
        );
        setWebsites(updatedWebsites);
    };

    const handleCancel = () => {
        reset({
            label: book?.label || "",
            author: book?.author || `${profile?.name} ${profile?.surname}`,
            description: book?.description || "",
            organisationName: "",
            name: "",
            surname: "",
            email: ""
        });

        setIncludePicture(initialState.includePicture);
        setIncludeBookDescription(initialState.includeBookDescription);
        setIncludeAuthorDescription(initialState.includeAuthorDescription);
        setWebsites(initialState.websites);
        setIsEditing(false);
    };

    const handleToggleModal = () => {

        setShowModal(!showModal);
    };


    const generateWidget = async () => {
        try {
            const payload = {
                bookId: book.id,
                hasPicture: includePicture,
                pictureUrl: book?.cover,
                hasDescription: includeBookDescription,
                hasAuthor: includeAuthorDescription,
                websites: websites,
                invitedBy: profile.id,
                organisationId: organisation.id,
                language: i18n.language
            };

            const response = await axios.post(`${API_BASE_URL}/invites/iframe-create`, payload);
            const correctedURL = response.request.responseURL.replace("/iframe-create", "/iframe-content");

            const widgetLink = `${correctedURL}/${response.data.widgetId}`;
            const iframeElement = `<iframe src="${widgetLink}" width="550" height="650"></iframe>`;
            setWidgetId(response.data.widgetId);
            setWidgetLink(iframeElement);
            setIsEditing(false);
            setWidgetExists(true);
            dispatch(snackbarSuccess("Your Widget has been succesfully updated!"));
        } catch (error) {
            console.error("Failed to create invitation:", error.response?.data || error.message);
        }
    };

    const deleteWidget = async () => {
        try {

            const url = `${API_BASE_URL}/invites/iframe-delete/${widgetId}`;
            const response = await axios.delete(url, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            setShowModal(!showModal);
            setWidgetLink("");
            setWidgetExists(false);
            dispatch(snackbarSuccess("Your Widget has been succesfully deleted!"));
            return response.data;
        } catch (error) {
            console.error("Error deleting resource:", error.response?.data || error.message);
            throw error;
        }
    };

    const selectFile = () => {
        fileRef.current.click();
    };

    const onFileSelected = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setCoverImage(URL.createObjectURL(file));

        try {

            const uploadedUrl = await uploadImage(file);

            setCoverImage(uploadedUrl);
        } catch (err) {
            console.error("Upload failed", err);
        }
    };
    const renderCover = () => {
        return (
            <div className="cover-logo">
                <input
                    className="hidden-file"
                    type="file"
                    onChange={onFileSelected}
                    ref={fileRef}
                />
                <img src={coverImage || require("assets/img/books-placeholder.png")} alt="Cover" />
                <div className="image-change">
                    <Icon
                        name={ICONS.CAMERA}
                        onClick={selectFile}
                    />
                </div>
            </div>
        );
    };
    return (
        <div className="section-general">
            <h2>{t("book.edit.widget.title")}</h2>
            <div className="container-buttons">
                <Button
                    spacedRight
                    outlined
                    iconRight={ICONS.PENCIL}
                    label={"Edit"}
                    onClick={() => setIsEditing(true)}
                />
            </div>
            <div className="divider" />
            {isEditing ? (
                <>
                    <div className="checkbox-container">
                        <FormControlLabel
                            label={<span className="non-selectable">{t("book.edit.widget.image")}</span>}
                            control={
                                <Checkbox
                                    checked={includePicture}
                                    onChange={({ target }) => setIncludePicture(target.checked)}
                                    name="hasPicture"
                                    color="primary"
                                />
                            }
                        />
                        <FormControlLabel
                            label={<span className="non-selectable">{t("book.edit.widget.author")}</span>}
                            control={
                                <Checkbox
                                    checked={includeAuthorDescription}
                                    onChange={({ target }) => setIncludeAuthorDescription(target.checked)}
                                    name="hasAuthor"
                                    color="primary"
                                />
                            }
                        />
                        <FormControlLabel
                            label={<span className="non-selectable">{t("book.edit.widget.description")}</span>}
                            control={
                                <Checkbox
                                    checked={includeBookDescription}
                                    onChange={({ target }) => setIncludeBookDescription(target.checked)}
                                    name="hasDescription"
                                    color="primary"
                                />
                            }
                        />
                    </div>
                    <div className="divider" />
                    <div className="cols-m">
                        {includePicture && renderCover()}
                        {includeAuthorDescription && (
                            <div className="col">
                                <InputText
                                    name="label"
                                    label={t("book.edit.label")}
                                    defaultValue={book.label}
                                    control={control}
                                    rules={{ required: true }}
                                    error={errors.label}
                                    dataTestId="label"
                                    disabled
                                />
                                <InputText
                                    name="author"
                                    label={t("book.edit.author")}
                                    defaultValue={book.author || `${profile?.name} ${profile?.surname}`}
                                    control={control}
                                    rules={{ required: true }}
                                    error={errors.author}
                                    dataTestId="author"
                                    disabled
                                />
                            </div>
                        )}
                    </div>
                    {includeBookDescription && (
                        <InputText
                            name="description"
                            label={t("book.edit.description")}
                            defaultValue={book.description}
                            control={control}
                            rules={{ required: true }}
                            error={errors.description}
                            multiline
                            dataTestId="description"
                            disabled
                        />
                    )}
                    <InputText
                        label={t("books.invite.organisationName")}
                        name="organisationName"
                        disabled
                    />
                    <div className="cols">
                        <div className="col">
                            <InputText
                                label={t("books.invite.name")}
                                name="name"
                                disabled
                            />
                        </div>
                        <div className="col">
                            <InputText
                                label={t("books.invite.surname")}
                                name="surname"
                                error={errors.surname}
                                disabled
                            />
                        </div>
                    </div>
                    <InputText
                        label={t("books.invite.email")}
                        name="email"
                        error={errors.email}
                        disabled
                    />
                    <div className="divider" />
                    <h3>{t("book.edit.widget.domains")}</h3>
                    <div className="spacer" />
                    {websites.map((website, index) => (
                        <div key={index} className="website-row">
                            <InputText
                                label={t("book.edit.widget.domain")}
                                name={`website-${index}`}
                                value={website.value}
                                onChange={(e) => handleChange(index, e.target.value)}
                            />
                            <Button
                                iconLeft={ICONS.CROSS}
                                secondary
                                onClick={() => handleRemoveWebsite(index)}
                            />
                        </div>
                    ))}
                    <div className="container-buttons">
                        <Button
                            spacedLeft
                            iconLeft={ICONS.PLUS}
                            label={t("book.edit.widget.website")}
                            onClick={handleAddWebsite}
                        />
                    </div>
                    <div className="button-container">
                        <Button
                            label={t("book.edit.widget.cancel.button")}
                            danger
                            onClick={handleCancel}
                        />
                        <Button
                            label={t("book.edit.widget.save.button")}
                            onClick={handleSubmit(generateWidget)}
                        />
                    </div>
                </>
            ) : (

                <>
                    <div className="cols-m">
                        {includePicture && renderCover()}
                        {includeAuthorDescription && (
                            <div className="col">
                                <InputText
                                    name="label"
                                    label={t("book.edit.label")}
                                    defaultValue={book.label}
                                    control={control}
                                    rules={{ required: true }}
                                    error={errors.label}
                                    dataTestId="label"
                                    disabled
                                />
                                <InputText
                                    name="author"
                                    label={t("book.edit.author")}
                                    defaultValue={book.author || `${profile?.name} ${profile?.surname}`}
                                    control={control}
                                    rules={{ required: true }}
                                    error={errors.author}
                                    dataTestId="author"
                                    disabled
                                />
                            </div>
                        )}
                    </div>
                    {includeBookDescription && (
                        <InputText
                            name="description"
                            label={t("book.edit.description")}
                            defaultValue={book.description}
                            control={control}
                            rules={{ required: true }}
                            error={errors.description}
                            multiline
                            dataTestId="description"
                            disabled
                        />
                    )}
                    <InputText
                        label={t("books.invite.organisationName")}
                        name="organisationName"
                        disabled
                    />
                    <div className="cols">
                        <div className="col">
                            <InputText
                                label={t("books.invite.name")}
                                name="name"
                                disabled
                            />
                        </div>
                        <div className="col">
                            <InputText
                                label={t("books.invite.surname")}
                                name="surname"
                                secondary
                                error={errors.surname}
                                disabled
                            />
                        </div>
                    </div>
                    <InputText
                        label={t("books.invite.email")}
                        name="email"
                        error={errors.email}
                        disabled
                    />
                    {widgetExists && (
                        <>
                            <div className="divider" />
                            <h3>{t("book.edit.widget.link")}</h3>
                            <div className="spacer" />
                            <InputText
                                label={"Link"}
                                name="widgetLink"
                                disabled
                                value={widgetLink}
                            />
                            <div className="container-buttons">
                                <Button
                                    spacedLeft
                                    danger
                                    label={t("book.edit.widget.delete.button")}
                                    onClick={handleToggleModal}
                                />
                            </div>
                        </>
                    )}

                    {showModal && <OkWarning
                        title={t("book.edit.widget.delete.button")}
                        message={t("book.widget.delete")}
                        onConfirm={deleteWidget}
                        onCancel={handleToggleModal}
                    />}

                </>
            )}
        </div>
    );
};

export default IntegrationForm;
